import { useLayoutEffect, type RefObject } from "react";

/** True where the browser sizes a textarea to its content from CSS (`field-sizing: content`). */
const nativeFieldSizing = () =>
  typeof CSS !== "undefined" &&
  typeof CSS.supports === "function" &&
  CSS.supports("field-sizing", "content");

/** Sets the textarea's height to fit its text, within its CSS min-height and max-height. */
function fit(el: HTMLTextAreaElement) {
  el.style.height = "auto";
  // border-box sizing: the height includes the borders, which scrollHeight does not.
  const style = getComputedStyle(el);
  const borders =
    (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.borderBottomWidth) || 0);
  el.style.height = `${el.scrollHeight + borders}px`;
}

/**
 * Grows a textarea to fit its text (issue #6). The CSS (`field-sizing: content` with a
 * max-height) does this natively; this is the fallback for browsers without it. It runs when the
 * value changes, so text from a co-editor, an undo or a loaded map fits too, not only typing, and
 * again when the field's width changes, because a narrower field wraps to more lines.
 */
export function useAutoGrow(ref: RefObject<HTMLTextAreaElement | null>, value: string) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || nativeFieldSizing()) return;
    fit(el);
  }, [ref, value]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || nativeFieldSizing() || typeof ResizeObserver === "undefined") return;
    let width = el.clientWidth;
    const observer = new ResizeObserver(() => {
      // Only width matters; reacting to our own height change would loop.
      if (el.clientWidth === width) return;
      width = el.clientWidth;
      fit(el);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
}
